/* eslint-disable react/no-this-in-sfc */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Highcharts from 'src/components/Highcharts';
import HighchartsReact from 'highcharts-react-official';
import { StacklineTheme, useStacklineTheme } from '@stackline/ui';
import { useAppSelector, useMetricFormatter } from 'src/utils/Hooks';
import ReactDOMServer from 'react-dom/server';
import { ChevronIconV2 } from 'src/components/SvgIcons';
import _get from 'lodash/get';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import _merge from 'lodash/merge';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { Box, SxProps } from '@mui/material';
import { getWeekLastDay } from 'src/utils/dateformatting';
import StackedTopEntityHoverTooltip from 'src/components/BeaconV2Components/TopEntityChartV2/StackedTopEnityHoverTooltip';
import _cloneDeep from 'lodash/cloneDeep';
import DefaultXAxisLabel from 'src/components/BeaconV2Components/TopEntityChartV2/DefaultXAxisLabel';
import { generateLink, shouldRenderLink } from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import { useRetailerId } from 'src/utils/Hooks/reduxSelectors';

const DEFAULT_TOP_ENTITY_HEIGHT = 420;

interface DataPoint {
  y: number;
  color: string;
}

interface MappedSeries {
  name: string;
  data: DataPoint[];
  borderRadiusTopLeft: string;
  borderRadiusTopRight: string;
  enableMouseTracking: boolean;
  cropThreshold: number;
  pointPadding: number;
  groupPadding: number;
  pointWidth: number;
}

interface StackedTopEntityChartProps {
  /**
   * Topentity data to display
   */
  chartSeries: (Highcharts.SeriesOptionsType & { metricType?: string } & { groupBy?: string })[];

  chartOptionsOverride?: Partial<Highcharts.Options> | ((defaultOptions: Highcharts.Options) => Highcharts.Options);
  redirectToSamePage?: boolean;
  /**
   * When true, (default) the bars will be left aligned, when false, the bars will be centered
   */
  leftAlignBars?: boolean;
  containerSx?: SxProps;

  /**
   * Optional formatter for the xAxis labels. Components must use ReactDOMServer.renderToStaticMarkup to render the label as stringified HTML.
   */
  xAxisFormatter?: (string) => string;

  categories?: string[];

  categoriesMetadata?: Map<string, { name: string; id: string; type: string }>;

  tooltipCategoryTitle?: string;

  /**
   * Locks last category to the right side of the chart during scrolling.
   */
  useStaticCategory?: boolean;

  /**
   * Reverses the starting scroll position of the xAxis. Default is false.
   */
  reverseAxisStartPosition?: boolean;

  /**
   * Max number of items to render in the chart. Defaults to 6.
   */
  maxNumberOfColumns?: number;

  metricType?: METRICTYPE;

  customTooltipBuilder?: (props: {
    point: Highcharts.Point;
    theme: StacklineTheme;
    tooltipCategoryTitle: string;
  }) => React.ReactNode;
}

/**
 * XAxisLabel for topEntityChart, ReactDOMServer.renderToStaticMarkup seems like it does not stringify nested ReactComponent, so used static html
 */
export const XAxisLabel = ({ name, link, newTab }: { name: string; link?: string; newTab?: boolean }) => {
  return (
    <>
      <a
        href={link}
        target={newTab ? '_blank' : undefined}
        rel={newTab ? 'noreferrer' : undefined}
        style={{
          textDecoration: link ? undefined : 'none',
          cursor: link ? 'pointer' : undefined
        }}
      >
        <div
          style={{
            fontSize: '12px',
            textAlign: 'center',
            width: '90px',
            wordBreak: 'break-word',
            height: '70px',
            whiteSpace: 'initial',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontFamily: 'Roboto',
            cursor: link ? 'pointer' : 'default'
          }}
          role="button"
        >
          <Text variant="body4" truncateLines={4} title={name}>
            {name}
          </Text>
        </div>
      </a>
    </>
  );
};

const PLOT_RENDER_TIMEOUT = 10;
const SLIDE_STEP = 5;
// const maxNumberOfColumns = 5;

/** Stacked TopEntity Chart (bar chart)
 * This graph is showing grouped data set, allowing specific series to be stacked on the same column.
 * Can be found on Reviews Tab.
 */
const StackedTopEntityChart = ({
  chartSeries,
  chartOptionsOverride,
  leftAlignBars = true,
  containerSx = {},
  tooltipCategoryTitle,
  xAxisFormatter,
  metricType = METRICTYPE.VOLUME,
  customTooltipBuilder,
  categories,
  categoriesMetadata = new Map(),
  useStaticCategory,
  reverseAxisStartPosition,
  maxNumberOfColumns = 6
}: StackedTopEntityChartProps) => {
  const theme = useStacklineTheme();
  const formatMetric = useMetricFormatter();
  const retailerId = useRetailerId();
  const { searchParams, additionalParams } = useAppSelector((state) => state.app.queryParams);
  const chartRef = useRef(null);
  const currentGroupBy = _get(chartSeries, [0, 'groupBy']);
  const totalDataPoints = chartSeries[0]?.data[0]?.data?.length - 1 || 0;
  const numberOfColumns = Math.max(maxNumberOfColumns - 1, 1);
  const initialRangeStart = reverseAxisStartPosition ? Math.max(totalDataPoints - numberOfColumns, 0) : 0;
  const [rangeStart, setRangeStart] = useState(initialRangeStart);
  const [dataLabelsEnabled, setDataLabelsEnabled] = useState(false);

  const currentMaxIndex = Math.min(
    rangeStart + numberOfColumns,
    leftAlignBars ? Math.max(5, totalDataPoints) : totalDataPoints
  );

  // Set time out is needed to prevent PLOT shows up before the chart renders
  const handleChartLoad = () => {
    setTimeout(() => {
      setDataLabelsEnabled(true);
    }, PLOT_RENDER_TIMEOUT);
  };

  // Set time out is needed to prevent PLOT shows up before the chart renders
  const handleChartRedraw = () => {
    setTimeout(() => {
      setDataLabelsEnabled(true);
    }, PLOT_RENDER_TIMEOUT);
  };

  const shiftLeft = () => {
    if (rangeStart >= SLIDE_STEP) {
      setRangeStart(rangeStart - SLIDE_STEP);
      setDataLabelsEnabled(false);
    } else if (rangeStart > 0) {
      setRangeStart(0);
      setDataLabelsEnabled(false);
    }
  };

  const shiftRight = () => {
    const newStart = Math.min(rangeStart + SLIDE_STEP, totalDataPoints - numberOfColumns);
    if (newStart > rangeStart) {
      setRangeStart(newStart);
      setDataLabelsEnabled(false);
    }
  };

  // Reset scrolled view when chart series changes
  useEffect(() => {
    setRangeStart(initialRangeStart);
  }, [chartSeries, initialRangeStart]);

  const textStyles: Highcharts.CSSObject = {
    fontFamily: theme.text.fontFamily,
    color: theme.colors.primary,
    fontSize: '12px'
  };

  const series: MappedSeries[] = chartSeries[0]?.data?.map(({ name, data }, index) => {
    // if the data is the first, this will stay at the top, and will apply border radius

    const shouldApplyBorderRadius = index === 0;

    return {
      name,
      data: _cloneDeep(data),
      borderRadiusTopLeft: shouldApplyBorderRadius ? '4px' : 0,
      borderRadiusTopRight: shouldApplyBorderRadius ? '4px' : 0,
      enableMouseTracking: true,
      cropThreshold: 1500,
      pointPadding: 0.1,
      groupPadding: 0.17,
      pointWidth: 24
    };
  });

  /**
   * If useStaticCategory is true, we will splice the last element of the categories into the currentMaxIndex found here. This will keep the last week on the right side of the chart no matter how much we scroll.
   */
  const categoriesWithStaticCategory = useMemo(() => {
    if (useStaticCategory) {
      if (series?.[0]?.data?.length > currentMaxIndex) {
        // Splice the last element of the categoryData into the currentMaxIndex found here
        const copyOfCategories = _cloneDeep(categories);
        // Splice the last element of the categories into the currentMaxIndex found here. This will keep the last week on the right side of the chart no matter how much we scroll.
        copyOfCategories.splice(currentMaxIndex, 1, categories[categories.length - 1]);
        return copyOfCategories;
      } else {
        return categories;
      }
    }

    return categories;
  }, [categories, useStaticCategory, series, currentMaxIndex]);

  /**
   * If useStaticCategory is true, we will splice the last element of the series data into the currentMaxIndex found here. This will keep the last week on the right side of the chart no matter how much we scroll.
   */
  const remappedChartSeriesForStaticCategory = useMemo(() => {
    if (useStaticCategory && series?.[0]?.data?.length > currentMaxIndex) {
      return series.map((s) => {
        const copyOfSeriesData = _cloneDeep(s?.data);

        copyOfSeriesData.splice(currentMaxIndex, 1, s?.data[s?.data?.length - 1]);
        return {
          ...s,
          data: copyOfSeriesData
        };
      });
    }
    return series;
  }, [series, currentMaxIndex, useStaticCategory]);

  const defaultOptions: Highcharts.Options = _merge(
    {
      chart: {
        animation: false,
        marginLeft: 10,
        marginTop: 0,
        type: 'column',
        height: DEFAULT_TOP_ENTITY_HEIGHT,
        events: {
          load: handleChartLoad,
          redraw: handleChartRedraw
        },
        spacingTop: 0,
        spacingBottom: 0
      },
      title: {
        text: ''
      },
      tooltip: {
        useHTML: true,
        animation: false,
        distance: 8,
        enabled: true,
        shadow: false,
        outside: true,
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        followPointer: true,
        formatter() {
          if (this.point.series) {
            if (customTooltipBuilder) {
              return customTooltipBuilder({
                point: this.point,
                theme,
                tooltipCategoryTitle
              });
            }
            return ReactDOMServer.renderToStaticMarkup(
              <StackedTopEntityHoverTooltip
                title={this.point.series.name}
                theme={theme}
                color={this.point.color}
                tooltipCategoryTitle={tooltipCategoryTitle}
                value={formatMetric(this.point.options.y, METRICTYPE.VOLUME, {
                  showFullValue: false,
                  decimalPlaces: 1
                })}
                percentage={formatMetric(this.point.percentage / 100, METRICTYPE.PERCENT, {
                  showFullValue: true,
                  decimalPlaces: 2
                })}
              />
            );
          }
          return null;
        }
      },

      xAxis: [
        {
          type: 'category',
          categories: useStaticCategory ? categoriesWithStaticCategory : categories,
          min: rangeStart,
          max: Math.min(rangeStart + numberOfColumns, leftAlignBars ? Math.max(5, totalDataPoints) : totalDataPoints),
          tickLength: 0,
          lineColor: 'transparent',
          title: {
            text: ''
          },
          style: {
            ...textStyles,
            whiteSpace: 'normal',
            textOverflow: 'none'
          },
          labels: {
            useHTML: true,
            formatter() {
              // Highcharts generates phantom numeric indices when the data has fewer
              // points than the minimum display width. Filter them out by checking
              // bounds rather than type, so legitimate numeric categories still render.
              const dataPointCount = categories?.length ?? (series?.[0]?.data?.length || 0);
              if (typeof this.value === 'number' && this.value >= dataPointCount) {
                return '';
              }
              if (xAxisFormatter) {
                return xAxisFormatter(this.value);
              }
              if (currentGroupBy === 'weekId') {
                return ReactDOMServer.renderToStaticMarkup(
                  <div style={{ position: 'relative' }}>
                    <XAxisLabel name={String(getWeekLastDay(this.value, false))} />
                  </div>
                );
              }

              return ReactDOMServer.renderToStaticMarkup(
                <div
                  style={{
                    whiteSpace: 'normal',
                    textOverflow: 'none',
                    position: 'relative',
                    ...(textStyles as React.CSSProperties)
                  }}
                >
                  <DefaultXAxisLabel
                    name={String(this.value)}
                    metricType={metricType}
                    link={
                      shouldRenderLink(currentGroupBy)
                        ? generateLink(
                            currentGroupBy,
                            retailerId,
                            this.value,
                            categoriesMetadata.get(this.value)?.id,
                            searchParams,
                            true,
                            additionalParams
                          )
                        : undefined
                    }
                    newTab={currentGroupBy === 'searchterm'}
                    id={categoriesMetadata.get(this.value)?.id}
                    currentGroupBy={currentGroupBy}
                    theme={theme}
                  />
                </div>
              );
            }
          }
        }
      ],
      credits: {
        enabled: false
      },
      legend: {
        enabled: false
      },
      yAxis: {
        stackLabels: {
          enabled: dataLabelsEnabled,
          padding: 10,
          crop: false,
          overflow: 'none',
          align: 'center',
          x: 0.05,
          formatter() {
            if (metricType === METRICTYPE.PERCENT) {
              // Hard coded 100% because it will always be 100% for percentage metrics (it is a stacked bar chart)
              return '100.0%';
            }
            return `${formatMetric(this.cumulative, metricType, {
              showFullValue: false,
              decimalPlaces: 1
            })}`;
          },
          style: {
            color: 'black',
            ...textStyles
          }
        },
        visible: true,
        gridLineWidth: 0,
        title: {
          text: ''
        },
        labels: {
          enabled: false
        },
        maxPadding: 0.2,
        min: 0,
        minRange: 0.1
      },
      series: useStaticCategory ? remappedChartSeriesForStaticCategory : series,

      plotOptions: {
        column: {
          stacking: 'normal',
          pointPadding: 0,
          groupPadding: 0,
          dataLabels: {
            enabled: false
          }
        },
        series: {
          borderColor: 'transparent',
          states: {
            inactive: { opacity: 1 },
            hover: {
              brightness: 0,
              animation: false,
              enabled: false
            }
          }
        }
      }
    },
    typeof chartOptionsOverride === 'function' ? {} : chartOptionsOverride
  );
  const options = typeof chartOptionsOverride === 'function' ? chartOptionsOverride(defaultOptions) : defaultOptions;

  const chartWrapperHeight = Number(options?.chart?.height ?? DEFAULT_TOP_ENTITY_HEIGHT);
  const chevronTopHeight =
    chartWrapperHeight <= DEFAULT_TOP_ENTITY_HEIGHT ? 50 - (DEFAULT_TOP_ENTITY_HEIGHT - chartWrapperHeight) * 0.05 : 50;

  return (
    <>
      <Box sx={{ position: 'relative', height: chartWrapperHeight, ...containerSx }}>
        <div
          style={{
            visibility: rangeStart === 0 ? 'hidden' : 'visible',
            position: 'absolute',
            top: `${chevronTopHeight}%`,
            left: '0',
            cursor: 'pointer'
          }}
          onClick={shiftLeft}
          role="button"
        >
          <ChevronIconV2 style={{ height: '24px', width: '24px', transform: 'rotate(180deg)' }} />
        </div>
        <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
        <div
          style={{
            visibility: rangeStart + numberOfColumns < totalDataPoints ? 'visible' : 'hidden',
            position: 'absolute',
            top: `${chevronTopHeight}%`,
            right: '0',
            cursor: 'pointer'
          }}
          role="button"
          onClick={shiftRight}
        >
          <ChevronIconV2 style={{ height: '24px', width: '24px' }} />
        </div>
      </Box>
    </>
  );
};

export default StackedTopEntityChart;
